// src/pages/Home.js — "Network Forge" v2
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box, Container, Typography, Button, Stack, Chip,
  Grid, Link, Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import RocketLaunchIcon        from "@mui/icons-material/RocketLaunch";
import MemoryIcon              from "@mui/icons-material/Memory";
import AdminPanelSettingsIcon  from "@mui/icons-material/AdminPanelSettings";
import ArrowForwardIcon        from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/useAuth";

// ── Palette Network Forge ────────────────────────────────────────────────────
const P = {
  bg:        "#0a0f0d",
  bgCard:    "#0f1612",
  bgCardHov: "#141c18",
  teal:      "#00e5c3",
  tealDim:   "#00e5c320",
  tealMid:   "#00e5c340",
  slate:     "#94a3a8",
  border:    "#1e2d28",
  borderGlow:"#00e5c330",
  white:     "#f0faf8",
  text:      "#c8d8d4",
};

// ── Fond circuit SVG inline ──────────────────────────────────────────────────
const CircuitBg = () => (
  <Box sx={{
    position: "absolute", inset: 0, overflow: "hidden",
    pointerEvents: "none", zIndex: 0,
  }}>
    <svg width="100%" height="100%" style={{ opacity: 0.06 }}>
      <defs>
        <pattern id="circuit" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
          <path d="M0 40 h20 m20 0 h40 M40 0 v20 m0 20 v40
                   M0 0 h4 M76 0 h4 M0 76 h4 M76 76 h4
                   M0 20 h8 M0 60 h8 M72 20 h8 M72 60 h8"
            stroke="#00e5c3" strokeWidth="0.8" fill="none" />
          <circle cx="40" cy="40" r="2.5" fill="#00e5c3" opacity="0.6" />
          <circle cx="20" cy="40" r="1.2" fill="#00e5c3" opacity="0.4" />
          <circle cx="60" cy="40" r="1.2" fill="#00e5c3" opacity="0.4" />
          <circle cx="40" cy="20" r="1.2" fill="#00e5c3" opacity="0.4" />
          <circle cx="40" cy="60" r="1.2" fill="#00e5c3" opacity="0.4" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#circuit)" />
    </svg>
    {/* Radial glow */}
    <Box sx={{
      position: "absolute", top: "10%", left: "50%",
      transform: "translateX(-50%)",
      width: 700, height: 400,
      background: "radial-gradient(ellipse at center, #00e5c310 0%, transparent 70%)",
      filter: "blur(40px)",
    }} />
  </Box>
);

// ── Ligne décorative ─────────────────────────────────────────────────────────
const TealLine = ({ width = 60, mt = 2 }) => (
  <Box sx={{
    width, height: 2, bgcolor: P.teal, mt,
    boxShadow: `0 0 8px ${P.teal}`,
  }} />
);

// ── Feature card ─────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, desc, delay = 0 }) {
  return (
    <Box sx={{
      height: "100%", p: 3.5,
      border: `1px solid ${P.border}`,
      borderRadius: 3, bgcolor: P.bgCard,
      position: "relative", overflow: "hidden",
      transition: "border-color .3s, transform .3s, box-shadow .3s",
      animation: `fadeSlideUp 0.6s ease both`,
      animationDelay: `${delay}ms`,
      "@keyframes fadeSlideUp": {
        from: { opacity: 0, transform: "translateY(24px)" },
        to:   { opacity: 1, transform: "translateY(0)" },
      },
      "&:hover": {
        borderColor: P.teal,
        transform: "translateY(-4px)",
        boxShadow: `0 12px 40px ${P.tealDim}`,
      },
      "&::before": {
        content: '""', position: "absolute",
        top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent, ${P.teal}, transparent)`,
        opacity: 0, transition: "opacity .3s",
      },
      "&:hover::before": { opacity: 1 },
    }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: 2,
        bgcolor: P.tealDim, border: `1px solid ${P.tealMid}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: P.teal, mb: 2.5,
        "& svg": { fontSize: 22 },
      }}>
        {icon}
      </Box>
      <Typography fontWeight={700} sx={{ color: P.white, mb: 1, fontSize: 16, letterSpacing: -.2 }}>
        {title}
      </Typography>
      <Typography sx={{ color: P.slate, fontSize: 14, lineHeight: 1.7 }}>
        {desc}
      </Typography>
    </Box>
  );
}

// ── Step card ────────────────────────────────────────────────────────────────
function StepCard({ number, title, desc, delay = 0 }) {
  return (
    <Box sx={{
      p: 3, borderRadius: 3,
      border: `1px solid ${P.border}`,
      bgcolor: P.bgCard,
      position: "relative",
      animation: `fadeSlideUp 0.6s ease both`,
      animationDelay: `${delay}ms`,
      "@keyframes fadeSlideUp": {
        from: { opacity: 0, transform: "translateY(24px)" },
        to:   { opacity: 1, transform: "translateY(0)" },
      },
      transition: "border-color .3s",
      "&:hover": { borderColor: P.tealMid },
    }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{
          width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
          border: `1.5px solid ${P.teal}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: P.teal, fontWeight: 800, fontSize: 14,
          fontFamily: "'DM Mono', monospace",
          boxShadow: `0 0 12px ${P.tealDim}`,
        }}>
          {String(number).padStart(2, "0")}
        </Box>
        <Box>
          <Typography fontWeight={700} sx={{ color: P.white, mb: 0.75, fontSize: 15 }}>
            {title}
          </Typography>
          <Typography sx={{ color: P.slate, fontSize: 13.5, lineHeight: 1.65 }}>
            {desc}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Home() {
  const { t, i18n }       = useTranslation();
  const { isAuthenticated } = useAuth();

  const features = React.useMemo(() => [
    { icon: <RocketLaunchIcon />,       title: t("home.features.smart_reservations"), desc: t("home.features.smart_reservations_desc") },
    { icon: <MemoryIcon />,             title: t("home.features.gns3_integration"),   desc: t("home.features.gns3_integration_desc")   },
    { icon: <AdminPanelSettingsIcon />, title: t("home.features.admin_view"),         desc: t("home.features.admin_view_desc")         },
  ], [i18n.language, t]);

  const steps = React.useMemo(() => [
    { title: t("home.steps.s1.title"), desc: t("home.steps.s1.desc") },
    { title: t("home.steps.s2.title"), desc: t("home.steps.s2.desc") },
    { title: t("home.steps.s3.title"), desc: t("home.steps.s3.desc") },
  ], [i18n.language, t]);

  const faqs = React.useMemo(() => [
    { q: t("home.faq.q1"), a: t("home.faq.a1") },
    { q: t("home.faq.q2"), a: t("home.faq.a2") },
    { q: t("home.faq.q3"), a: t("home.faq.a3") },
  ], [i18n.language, t]);

  return (
    <Box sx={{ bgcolor: P.bg, color: P.text, minHeight: "100vh" }}>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <Box sx={{ position: "relative", overflow: "hidden",
        borderBottom: `1px solid ${P.border}` }}>
        <CircuitBg />
        <Container maxWidth="lg" sx={{ py: { xs: 10, md: 16 }, position: "relative", zIndex: 1 }}>
          <Stack spacing={4} alignItems="center" textAlign="center">

            {/* Badge */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 2, py: 0.75, borderRadius: 20,
              border: `1px solid ${P.tealMid}`,
              bgcolor: P.tealDim,
              animation: "fadeIn .8s ease both",
              "@keyframes fadeIn": { from: { opacity: 0, transform: "translateY(-8px)" }, to: { opacity: 1, transform: "none" } },
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: P.teal,
                boxShadow: `0 0 6px ${P.teal}`,
                animation: "blink 2s ease-in-out infinite",
                "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: .3 } },
              }} />
              <Typography sx={{ color: P.teal, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>
                {t("app.title")}
              </Typography>
            </Box>

            {/* Titre hero */}
            <Box sx={{
              animation: "fadeSlideUp .8s ease .15s both",
              "@keyframes fadeSlideUp": {
                from: { opacity: 0, transform: "translateY(30px)" },
                to:   { opacity: 1, transform: "none" },
              },
            }}>
              <Typography component="h1" sx={{
                fontSize: { xs: "2.4rem", md: "3.8rem", lg: "4.6rem" },
                fontWeight: 900, lineHeight: 1.05,
                letterSpacing: { xs: -1, md: -2 },
                color: P.white,
                "& span": { color: P.teal, textShadow: `0 0 40px ${P.tealMid}` },
              }}>
                {t("home.hero.title").split(" ").map((word, i) =>
                  ["GNS3","NextHopLab","Labs","réseau","lab"].some(k => word.toLowerCase().includes(k.toLowerCase()))
                    ? <span key={i}>{word} </span>
                    : word + " "
                )}
              </Typography>
            </Box>

            {/* Sous-titre */}
            <Typography sx={{
              maxWidth: 620, color: P.slate, fontSize: { xs: 15, md: 17 },
              lineHeight: 1.7,
              animation: "fadeSlideUp .8s ease .3s both",
            }}>
              {t("home.hero.subtitle")}
            </Typography>

            {/* CTA */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{
              animation: "fadeSlideUp .8s ease .45s both",
            }}>
              <Button component={RouterLink} to="/labs" variant="contained" size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: P.teal, color: P.bg,
                  fontWeight: 800, fontSize: 14, letterSpacing: .5,
                  px: 4, py: 1.5, borderRadius: 2,
                  boxShadow: `0 0 24px ${P.tealMid}`,
                  "&:hover": {
                    bgcolor: "#00cdb0",
                    boxShadow: `0 0 40px ${P.tealMid}`,
                    transform: "translateY(-1px)",
                  },
                  transition: "all .2s",
                }}>
                {t("home.hero.cta_primary")}
              </Button>
              <Button component={RouterLink} to="/reservations" variant="outlined" size="large"
                sx={{
                  color: P.white, borderColor: P.border,
                  fontWeight: 700, fontSize: 14, px: 4, py: 1.5, borderRadius: 2,
                  "&:hover": { borderColor: P.teal, color: P.teal, bgcolor: P.tealDim },
                  transition: "all .2s",
                }}>
                {t("home.hero.cta_secondary")}
              </Button>
            </Stack>

            {/* Login/Register link */}
            {!isAuthenticated && (
              <Typography sx={{
                color: P.slate, fontSize: 13,
                animation: "fadeSlideUp .8s ease .6s both",
              }}>
                {t("home.hero.subcta")}{" "}
                <Link component={RouterLink} to="/login"
                  sx={{ color: P.teal, textDecoration: "none",
                    "&:hover": { textDecoration: "underline" } }}>
                  {t("nav.login")}
                </Link>
                {" "}{t("common.or")}{" "}
                <Link component={RouterLink} to="/register"
                  sx={{ color: P.teal, textDecoration: "none",
                    "&:hover": { textDecoration: "underline" } }}>
                  {t("nav.register")}
                </Link>
              </Typography>
            )}
          </Stack>
        </Container>
      </Box>

      {/* ── FEATURES ─────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack alignItems="center" sx={{ mb: 6 }}>
          <Typography sx={{
            color: P.teal, fontSize: 11, fontWeight: 700,
            letterSpacing: 2.5, textTransform: "uppercase", mb: 1.5,
          }}>
            {t("home.sections.features")}
          </Typography>
          <TealLine width={48} mt={0} />
        </Stack>
        <Grid container spacing={3}>
          {features.map((f, i) => (
            <Grid item xs={12} md={4} key={i}>
              <FeatureCard {...f} delay={i * 120} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── STEPS ────────────────────────────────────────────────────── */}
      <Box sx={{ borderTop: `1px solid ${P.border}`, borderBottom: `1px solid ${P.border}`,
        bgcolor: "#080c0a" }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Stack alignItems="center" sx={{ mb: 6 }}>
            <Typography sx={{
              color: P.teal, fontSize: 11, fontWeight: 700,
              letterSpacing: 2.5, textTransform: "uppercase", mb: 1.5,
            }}>
              {t("home.sections.steps")}
            </Typography>
            <TealLine width={48} mt={0} />
          </Stack>
          <Stack spacing={2} sx={{ maxWidth: 640, mx: "auto" }}>
            {steps.map((s, i) => (
              <StepCard key={i} number={i + 1} title={s.title} desc={s.desc} delay={i * 100} />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── STATS STRIP ──────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: `1px solid ${P.border}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={3} justifyContent="center">
            {[
              { val: "GNS3",    label: "Powered by" },
              { val: "100%",    label: "Réseau réel" },
              { val: "24/7",    label: "Disponibilité" },
              { val: "CCNA+",   label: "Certifications" },
            ].map(({ val, label }) => (
              <Grid item xs={6} md={3} key={label}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography sx={{
                    fontSize: { xs: "1.8rem", md: "2.4rem" },
                    fontWeight: 900, color: P.teal, letterSpacing: -1,
                    fontFamily: "'DM Mono', 'Fira Code', monospace",
                    textShadow: `0 0 20px ${P.tealMid}`,
                  }}>
                    {val}
                  </Typography>
                  <Typography sx={{ color: P.slate, fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>
                    {label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack alignItems="center" sx={{ mb: 6 }}>
          <Typography sx={{
            color: P.teal, fontSize: 11, fontWeight: 700,
            letterSpacing: 2.5, textTransform: "uppercase", mb: 1.5,
          }}>
            {t("home.sections.faq")}
          </Typography>
          <TealLine width={48} mt={0} />
        </Stack>
        <Stack spacing={1.5}>
          {faqs.map((f, i) => (
            <Accordion key={i} disableGutters elevation={0} sx={{
              bgcolor: P.bgCard,
              border: `1px solid ${P.border}`,
              borderRadius: "12px !important",
              "&:before": { display: "none" },
              "&.Mui-expanded": { borderColor: P.tealMid },
              transition: "border-color .2s",
            }}>
              <AccordionSummary expandIcon={
                <ExpandMoreIcon sx={{ color: P.teal, fontSize: 20 }} />
              } sx={{ px: 3, py: 1.5, minHeight: 0,
                "& .MuiAccordionSummary-content": { my: 1 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CheckCircleOutlineIcon sx={{ fontSize: 15, color: P.teal, flexShrink: 0 }} />
                  <Typography fontWeight={600} sx={{ color: P.white, fontSize: 14.5 }}>
                    {f.q}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5, pt: 0 }}>
                <Typography sx={{ color: P.slate, fontSize: 14, lineHeight: 1.7, pl: 3.5 }}>
                  {f.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Container>

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <Box sx={{ borderTop: `1px solid ${P.border}`, bgcolor: "#070b09" }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
            justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: P.teal,
                boxShadow: `0 0 6px ${P.teal}` }} />
              <Typography sx={{ color: P.slate, fontSize: 13 }}>
                © {new Date().getFullYear()} {t("home.footer.rights")}
              </Typography>
            </Stack>
            <Typography sx={{ color: P.slate, fontSize: 12, letterSpacing: .3 }}>
              {t("home.footer.tagline")}
            </Typography>
            <Stack direction="row" spacing={3}>
              {[
                { to: "/labs",         label: t("home.footer.links.labs")         },
                { to: "/reservations", label: t("home.footer.links.reservations") },
                { to: "/dashboard",    label: t("home.footer.links.dashboard")    },
              ].map(({ to, label }) => (
                <Link key={to} component={RouterLink} to={to} underline="none"
                  sx={{ color: P.slate, fontSize: 13,
                    "&:hover": { color: P.teal }, transition: "color .2s" }}>
                  {label}
                </Link>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
}
